import Loading from 'common/components/loading/loading';
import { CustomTable } from 'common/components/table/customTable';
import TileWithoutDefaultStyling from 'features/tiles/tileWithoutDefaultStyling';
import { FC, useEffect, useMemo, useState } from 'react';
import ReportsInteractionBar from './reportsInteractionBar';
import { useDispatch, useSelector } from 'react-redux';
import { DropDownItemType } from 'common/components/drop-downs/legacy/dropDownItemType';
import { ReportExport } from './types/report-export';
import { getMetricReportColumns } from './reports-column-definitions';
import {
  createTotalsMetricReport,
  metricAttributionsToMetricReports
} from './metric-attribution-mapper';
import { MetricAttribution } from 'common/types/metric.models';
import {
  onNextPage,
  onPageSizeChanged,
  onPreviousPage,
  selectReportsTableState
} from './reports-table.slice';
import { RootState } from 'app/store';
import { selectCurrencyCode } from 'features/settings/settingsSlice';
import { createComponentId } from './functions/reports-functions';
import {
  ColumnResizeMode,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable
} from '@tanstack/react-table';
import { getSearchTermsById } from './../search-bar/searchBarSlice';
import { getDateRangeAsObjects } from 'features/date-range-selection/dateRangeSlice';
import { fuzzyFilter } from 'common/components/table/table-functions/filters';
import { Pagination } from 'common/components/pagination/pagination';
import { getSelectedAttributionModel } from 'features/attribute-model/availableAttributionModelsSlice';
import { CustomTablePaging } from 'common/components/table/tableTypes';
import {
  selectIsAttributionComparisonActive,
  getSelectedAttributionCompareModel
} from 'features/attribute-compare-model/availableAttributionCompareModelsSlice';
import { UseReportQueriesResult } from './hooks/use-report-queries';
import {
  createRows,
  createTotalRow,
  createAllMetricAttributionsFromArMetricRows
} from 'features/advanced-reports/table/factories/advanced-report-table-row-factory';
import { AdvancedReportMetricRowsResult } from 'features/advanced-reports/advanced-reports.types';
import { sortRows } from 'features/advanced-reports/table/factories/advanced-report-table-helpers';
import { fieldColumnId } from 'features/advanced-reports/table/advanced-reports-column-defs';

interface ReportsTableProps {
  reportQueryResults: UseReportQueriesResult;
  reportType?: string;
}

export const ReportsTable: FC<ReportsTableProps> = ({
  reportQueryResults,
  reportType
}) => {
  const dispatch = useDispatch();
  const [columnResizeMode] = useState<ColumnResizeMode>('onChange');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [textFilter, setTextFilter] = useState<string>('');

  const reportsTableState = useSelector(selectReportsTableState);

  const { tableValueToSearch } = useSelector((state: RootState) =>
    getSearchTermsById(state, createComponentId(reportType))
  );
  const selectedCurrencyCode = useSelector(selectCurrencyCode);

  const { isPocModeActive } = useSelector(
    (state: RootState) => state.pocToggle
  );

  const selectedAttributionModel = useSelector((state: RootState) =>
    getSelectedAttributionModel(state, isPocModeActive)
  );

  const displayComparisonAttributionModelData = useSelector(
    (state: RootState) => selectIsAttributionComparisonActive(state)
  );

  const selectedAttributionCompareModel = useSelector((state: RootState) =>
    getSelectedAttributionCompareModel(state, isPocModeActive)
  );

  const dates = useSelector(getDateRangeAsObjects);

  useEffect(() => {
    setTextFilter(tableValueToSearch);
  }, [tableValueToSearch]);

  const primaryRowsResult: AdvancedReportMetricRowsResult = useMemo(() => {
    return {
      calls: reportQueryResults.main.calls.data,
      impressions: reportQueryResults.main.impressions.data,
      clicks: reportQueryResults.main.clicks.data,
      costs: reportQueryResults.main.cost.data,
      opps: reportQueryResults.main.opps.data,
      oppValues: reportQueryResults.main.oppValues.data,
      revenues: reportQueryResults.main.revenues.data,
      sales: reportQueryResults.main.sales.data,
      webs: reportQueryResults.main.forms.data
    };
  }, [reportQueryResults]);

  const compareRowsResult: AdvancedReportMetricRowsResult = useMemo(() => {
    return {
      calls: reportQueryResults.compare.calls.data,
      impressions: reportQueryResults.compare.impressions.data,
      clicks: reportQueryResults.compare.clicks.data,
      costs: reportQueryResults.compare.cost.data,
      opps: reportQueryResults.compare.opps.data,
      oppValues: reportQueryResults.compare.oppValues.data,
      revenues: reportQueryResults.compare.revenues.data,
      sales: reportQueryResults.compare.sales.data,
      webs: reportQueryResults.compare.forms.data
    };
  }, [reportQueryResults]);

  const metricRows = useMemo(() => {
    const metricRows = createRows(primaryRowsResult, compareRowsResult);
    const totalRow = createTotalRow(metricRows);

    const allRows = [...metricRows, totalRow];

    const sortedRows =
      sorting.length > 0
        ? sortRows(allRows, sorting[0].id, sorting[0].desc)
        : sortRows(allRows, fieldColumnId, false);

    return sortedRows;
  }, [primaryRowsResult, compareRowsResult, sorting]);

  const totalRow = useMemo(() => {
    const totalRow = createTotalRow(metricRows);
    return totalRow;
  }, [metricRows]);

  const shouldBlurFieldColumn = useMemo(() => {
    return reportType?.toUpperCase() !== 'SOURCE';
  }, [reportType]);

  const columnDefs = useMemo(() => {
    return getMetricReportColumns(
      totalRow,
      shouldBlurFieldColumn,
      displayComparisonAttributionModelData,
      reportQueryResults,
      selectedCurrencyCode
    );
  }, [
    totalRow,
    selectedCurrencyCode,
    shouldBlurFieldColumn,
    displayComparisonAttributionModelData,
    reportQueryResults
  ]);

  const metricReports = useMemo(() => {
    const metricAttributions = createAllMetricAttributionsFromArMetricRows(
      selectedAttributionModel,
      reportQueryResults.main.impressions.data,
      reportQueryResults.main.clicks.data,
      reportQueryResults.main.calls.data,
      reportQueryResults.main.forms.data,
      reportQueryResults.main.opps.data,
      reportQueryResults.main.oppValues.data,
      reportQueryResults.main.sales.data,
      reportQueryResults.main.revenues.data,
      reportQueryResults.main.cost.data
    );

    return metricAttributionsToMetricReports(
      metricAttributions,
      selectedAttributionModel,
      selectedAttributionCompareModel.id
    );
  }, [
    reportQueryResults,
    selectedAttributionModel,
    selectedAttributionCompareModel
  ]);

  const reportExport = useMemo(() => {
    const reportExport: ReportExport = {
      orderedReportColumnIds: columnDefs.map((column) => column.id as string),
      metricReports,
      dates,
      currencyCode: selectedCurrencyCode,
      searchTerm: tableValueToSearch,
      attributionModel: selectedAttributionModel
    };
    return reportExport;
  }, [
    columnDefs,
    dates,
    selectedCurrencyCode,
    tableValueToSearch,
    selectedAttributionModel,
    metricReports
  ]);

  const reportTable = useReactTable({
    data: metricRows,
    columns: columnDefs,
    manualSorting: true,
    state: {
      sorting,
      globalFilter: textFilter,
      pagination: {
        pageIndex: reportsTableState.currentPage - 1, // TanStack uses a zero-based index for paging
        pageSize: reportsTableState.currentPageSize
      }
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setTextFilter,
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: fuzzyFilter,
    enableColumnResizing: true,
    columnResizeMode,
    debugTable: false,
    debugHeaders: false,
    debugColumns: false
  });

  const numberOfTableRows = reportTable.getFilteredRowModel().rows.length;

  const paging = useMemo(() => {
    const paging: CustomTablePaging = {
      isSticky: true,
      currentPage: reportsTableState.currentPage,
      currentPageSize: reportsTableState.currentPageSize,
      pageSizes: reportsTableState.pageSizes.map<DropDownItemType>(
        (pageSize) => {
          const pageSizeString = pageSize.toString();
          return {
            key: pageSizeString,
            label: pageSizeString,
            isActive: pageSize === reportsTableState.currentPageSize
          };
        }
      ),
      totalRecords: numberOfTableRows
    };

    return paging;
  }, [reportsTableState, numberOfTableRows]);

  return (
    <>
      <div className="w-full">
        <ReportsInteractionBar
          componentId={createComponentId(reportType)}
          reportExport={reportExport}
        />
      </div>
      {metricRows && totalRow ? (
        <TileWithoutDefaultStyling className="min-h-fit justify-center align-middle bg-white border rounded border-off-navy mb-2 grow basis-full">
          <>
            <CustomTable
              key={`${reportType}-table`}
              componentId={createComponentId(reportType)}
              table={reportTable}
              overflowClass="max-sm:overflow-auto"
            />
            {/* 
                  // We do not have a solution for when the paging is sticky and the table is too wide for devices
                  // There are solutions, see: https://stackoverflow.com/a/70772702/21681884
                  // An alternative would be to create a flexible table which has no overflow-x on it (remove Detailed views) or move paging outside the table 
              */}
            <Pagination
              currentPage={paging.currentPage}
              currentPageSize={paging.currentPageSize}
              totalPages={reportTable.getPageCount()}
              totalRecords={paging.totalRecords}
              pageSizes={paging.pageSizes}
              isSticky={paging.isSticky}
              onPreviousPage={() => dispatch(onPreviousPage())}
              onNextPage={() => dispatch(onNextPage())}
              onPageSizeChanged={(pagesize) =>
                dispatch(onPageSizeChanged(pagesize))
              }
            ></Pagination>
          </>
        </TileWithoutDefaultStyling>
      ) : (
        <></>
      )}
    </>
  );
};
export default ReportsTable;
